import React, { useContext, useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Group,
  Space,
  Title,
  Card,
  Grid,
  Tooltip,
  Badge,
  ThemeIcon,
  Menu,
  Button,
  Container,
  Stack,
  Text,
  ActionIcon,
  Table,
  Box,
  TextInput,
  Select,
  Pagination,
  LoadingOverlay,
  Modal,
  Alert,
  Code,
  ScrollArea,
  Collapse,
} from "@mantine/core";
import {
  IconDownload,
  IconEye,
  IconUpload,
  IconPlus,
  IconCalendarRepeat,
  IconSettings,
  IconFileText,
  IconSearch,
  IconCheck,
  IconX,
  IconClock,
  IconFilter,
  IconRefresh,
  IconFileSpreadsheet,
  IconAlertTriangle,
  IconCircleCheck,
  IconExclamationCircle,
  IconArrowBackUp,
  IconEdit,
  IconTrash,
  IconCloudDownload,
} from "@tabler/icons-react";
import {
  collection,
  DocumentData,
  Firestore,
  getCountFromServer,
  getDocs,
  limit,
  orderBy,
  QueryDocumentSnapshot,
  query,
  startAfter,
  Unsubscribe,
  where,
  doc,
  updateDoc,
  Timestamp,
  onSnapshot,
  deleteDoc,
} from "firebase/firestore";
import { useFirestore, useFunctions } from "../../helpers/firebaseContext";
import { httpsCallable } from "firebase/functions";
import { ConfigContext, MyUserDataContext } from "../../contexts";
import { LsegCorporateActionsModal } from "./components/LsegCorporateActionsModal";
import { notifications } from "@mantine/notifications";
import {
  BulkGenevaRequest,
  GenevaSupportingData,
  GenevaSupportingFieldMap,
} from '../../types/geneva-schema';
import {
  normalizeSupportingRecords,
  collectFieldSummaries,
  summaryMatchesCode,
  summaryMatchesQuery,
  getDisplayNameFromSummary,
  getDisplayCodeFromSummary,
  flattenSupportingSummaries,
  getSummaryDetails,
} from '../../utils/geneva-supporting-data';
import { BaseResult, GenerateXmlRequest, GenevaResponse, SingleGenevaResponseData, BulkGenevaResponseData, CustomClaims } from '../../types';
import { BulkDataPreview } from '../../components/BulkDataPreview';

interface GenevaRequest {
  id: string;
  orgId: string;
  templateId: string;
  status: 'draft' | 'pending' | 'approved' | 'processing' | 'completed' | 'error';
  metadata: {
    recordType: string;
    isMultiRecord?: boolean;
    fieldCount: number;
    arrayFieldCount?: number;
  };
  formData: Record<string, any>;
  supportingData?: GenevaSupportingData;
  audit: {
    createdAt: number;
    createdBy: string;
    createdByEmail: string;
    createdByName?: string;
    updatedAt: number;
    updatedBy: string;
    updatedByEmail: string;
    updatedByName?: string;
    approvedAt?: number;
    approvedBy?: string;
    approvedByEmail?: string;
    approvedByName?: string;
    // rejection fields removed
  };
  createdAt: Timestamp;
  updatedAt: Timestamp;
  geneva_response?: GenevaResponse;
  geneva_responses?: Record<string, GenevaResponse>; // For multi-record requests
  isBulk?: boolean;
  lsegCa?: { key: string; type: 'Dividend' | 'Split'; investment: string; exDate: string };
}

type GenevaLoaderError = {
  message: string;
  code?: string | null;
  faultCode?: string | null;
  faultString?: string | null;
  rawResponse?: string | null;
  xmlContent?: string;
  requestId?: string;
  statusCode?: number;
  operation?: string;
};

const getNonEmptyString = (value: unknown): string | undefined => {
  return typeof value === 'string' && value.trim().length > 0 ? value : undefined;
};

const normalizeGenevaXmlError = (error: unknown, fallbackRequestId: string): GenevaLoaderError => {
  const firebaseError = error as {
    code?: unknown;
    message?: unknown;
    details?: unknown;
  };
  const rawDetails = firebaseError?.details;
  const details = rawDetails && typeof rawDetails === 'object'
    ? rawDetails as Record<string, unknown>
    : {};

  const detailMessage = getNonEmptyString(details.message)
    || getNonEmptyString(details.detailMessage)
    || getNonEmptyString(rawDetails);
  const errorMessage = error instanceof Error
    ? error.message
    : getNonEmptyString(error);
  const finalMessage = detailMessage || errorMessage || 'Failed to generate XML';
  const statusCode = typeof details.statusCode === 'number' ? details.statusCode : undefined;

  return {
    message: finalMessage,
    code: getNonEmptyString(firebaseError?.code) ?? null,
    faultCode: getNonEmptyString(details.faultCode) ?? null,
    faultString: getNonEmptyString(details.faultString) ?? null,
    rawResponse: getNonEmptyString(details.rawResponse) ?? null,
    xmlContent: getNonEmptyString(details.xmlContent),
    requestId: getNonEmptyString(details.requestId) || fallbackRequestId,
    statusCode,
    operation: getNonEmptyString(details.operation),
  };
};

// Friendly rendering helpers for form data
const prettifyLabel = (rawKey: string): string => {
  const key = rawKey.replace(/^_+/, '').replace(/[-_]+/g, ' ');
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const isLikelyIsoDate = (value: string): boolean => {
  if (typeof value !== 'string') return false;
  if (!/\d{4}-\d{2}-\d{2}T/.test(value)) return false;
  const time = Date.parse(value);
  return !Number.isNaN(time);
};

const formatNumberLike = (value: string | number): string => {
  const num = typeof value === 'number' ? value : parseFloat(value);
  if (Number.isNaN(num)) return String(value);
  const decimals = typeof value === 'string' && value.includes('.') ? value.split('.')[1].length : 0;
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: Math.min(decimals, 6),
    maximumFractionDigits: Math.max(decimals, 6),
  }).format(num);
};

const formatValueForDisplay = (value: any, key?: string): React.ReactNode => {
  if (value === null || value === undefined || value === '') return '—';

  // Treat portfolio-like keys as identifiers (no number formatting)
  if (key && /portfolio/i.test(key)) {
    return String(value);
  }

  // Try to parse array-like JSON strings for known list fields (e.g., Adj Lines)
  const tryParseArrayLike = (str: string): any[] | null => {
    const s = str.trim();
    try {
      if (s.startsWith('[') && s.endsWith(']')) {
        const arr = JSON.parse(s);
        return Array.isArray(arr) ? arr : null;
      }
      // Handle multiple objects without surrounding brackets
      if ((s.startsWith('{') && s.endsWith('}')) && s.includes('},{')) {
        const arr = JSON.parse(`[${s}]`);
        return Array.isArray(arr) ? arr : null;
      }
    } catch { }
    return null;
  };

  if (typeof value === 'string' && (key && /(lines|items|entries)/i.test(key))) {
    const parsed = tryParseArrayLike(value);
    if (parsed) value = parsed; // fall through to array renderer below
  }

  if (typeof value === 'string' && isLikelyIsoDate(value)) {
    const d = new Date(value);
    return d.toLocaleString();
  }

  if (
    typeof value === 'number' ||
    (typeof value === 'string' && /^-?\d+(\.\d+)?$/.test(value))
  ) {
    return formatNumberLike(value as any);
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return '—';

    const allObjects = value.every((v) => v && typeof v === 'object' && !Array.isArray(v));
    if (allObjects) {
      const columnSet = new Set<string>();
      value.forEach((row) => Object.keys(row).forEach((k) => columnSet.add(k)));
      const columns = Array.from(columnSet);
      columns.sort((a, b) => {
        if (a === 'LineNumber') return -1;
        if (b === 'LineNumber') return 1;
        return a.localeCompare(b);
      });

      return (
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              {columns.map((col) => (
                <Table.Th key={col}>
                  <Text size="xs" c="dimmed">{prettifyLabel(col)}</Text>
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {value.map((row: any, idx: number) => (
              <Table.Tr key={idx}>
                {columns.map((col) => (
                  <Table.Td key={col}>
                    <Text size="xs" style={{ whiteSpace: 'pre-wrap' }}>
                      {typeof row[col] === 'object'
                        ? JSON.stringify(row[col])
                        : String(row[col] ?? '—')}
                    </Text>
                  </Table.Td>
                ))}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      );
    }

    // Primitive arrays
    return value
      .map((v) => (typeof v === 'object' ? JSON.stringify(v) : String(v)))
      .join(', ');
  }

  if (typeof value === 'object') {
    // Shallow render nested object
    return (
      <Stack gap={2}>
        {Object.entries(value).map(([k, v]) => (
          <Group wrap="nowrap" gap="xs" key={k} align="flex-start">
            <Text size="xs" c="dimmed" style={{ minWidth: 140 }}>{prettifyLabel(k)}</Text>
            <Text size="xs" style={{ whiteSpace: 'pre-wrap' }}>
              {typeof v === 'object' ? JSON.stringify(v) : String(v)}
            </Text>
          </Group>
        ))}
      </Stack>
    );
  }
  return String(value);
};

const buildSortedEntries = (data: Record<string, any>): [string, any][] => {
  const priority = [
    'Portfolio',
    'LocationAccount',
    'EventDate',
    'Investment',
    'CounterInvestment',
    'Quantity',
    'NetCounterAmount',
  ];
  const keys = Object.keys(data).filter((k) => !k.startsWith('_') && k !== 'records');
  return keys
    .sort((a, b) => {
      const ia = priority.indexOf(a);
      const ib = priority.indexOf(b);
      if (ia !== -1 && ib !== -1) return ia - ib;
      if (ia !== -1) return -1;
      if (ib !== -1) return 1;
      return a.localeCompare(b);
    })
    .map((k) => [k, (data as any)[k]] as [string, any]);
};

const buildMultiTransactionEntries = (data: Record<string, any>, supporting?: GenevaSupportingFieldMap) => {
  const baseEntries = buildSortedEntries(data);
  const extras: any[] = Array.isArray((data as any)._multiTransactions) ? (data as any)._multiTransactions : [];

  const rows: {
    label: string;
    value: any;
    supporting?: GenevaSupportingFieldMap;
    transactionIndex: number;
  }[] = [];

  baseEntries.forEach(([key, value]) => {
    if (key === '_multiTransactions') {
      return;
    }
    rows.push({
      label: prettifyLabel(key),
      value,
      supporting,
      transactionIndex: 1,
    });
  });

  if (extras.length > 0) {
    extras.forEach((txn, idx) => {
      const txnEntries = buildSortedEntries(txn as Record<string, any>);
      txnEntries.forEach(([key, value]) => {
        rows.push({
          label: `Transaction ${idx + 2} – ${prettifyLabel(key)}`,
          value,
          supporting,
          transactionIndex: idx + 2,
        });
      });
    });
  }

  return rows;
};

interface KeyValueListProps {
  data: Record<string, any>;
  supporting?: GenevaSupportingFieldMap;
}

const TRANSACTION_COLORS = [
  'blue.6',
  'teal.6',
  'violet.6',
  'orange.6',
  'grape.6',
  'cyan.6',
  'lime.6',
];

const KeyValueList: React.FC<KeyValueListProps> = ({ data, supporting }) => {
  const rows = buildMultiTransactionEntries(data, supporting);
  return (
    <Stack gap="xs">
      {rows.map(({ label, value, transactionIndex }, idx) => {
        const summaryKey = label.replace(/^Transaction \d+ – /, '');
        const entrySupporting = supporting?.[summaryKey];
        const rendered = formatValueForDisplay(value, summaryKey);
        const isPrimitive = typeof rendered === 'string' || typeof rendered === 'number';
        const primitiveText = isPrimitive ? String(rendered) : null;
        const details = entrySupporting ? getSummaryDetails(entrySupporting) : null;
        const supportingName = details?.name || details?.description || null;
        const supportingCode = details?.code || null;
        const showName = supportingName && supportingName !== primitiveText;
        const showCode = !showName && supportingCode && supportingCode !== primitiveText;
        const colorIndex = Math.min(transactionIndex - 1, TRANSACTION_COLORS.length - 1);
        const labelColor = TRANSACTION_COLORS[colorIndex];

        return (
          <Group wrap="nowrap" gap="sm" key={`${label}-${idx}`} align="flex-start">
            <Text size="sm" fw={600} c={labelColor} style={{ minWidth: 200 }}>
              {label}
            </Text>
            {isPrimitive ? (
              <Stack gap={2} style={{ flex: 1 }}>
                <Text size="sm" fw={500} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {rendered as any}
                </Text>
                {(showName || showCode) && (
                  <Group gap={6} wrap="nowrap">
                    {showName && supportingName && (
                      <Text size="xs" c="dimmed" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {supportingName}
                      </Text>
                    )}
                    {showCode && supportingCode && (
                      <Badge size="xs" variant="outline" color="gray">
                        {supportingCode}
                      </Badge>
                    )}
                  </Group>
                )}
              </Stack>
            ) : (
              <Box style={{ maxWidth: '100%', overflowX: 'auto' }}>
                {rendered}
              </Box>
            )}
          </Group>
        );
      })}
    </Stack>
  );
};

// Extract all Portfolio values from nested or multi-record formData
const extractPortfolios = (input: any): string[] => {
  const result: string[] = [];
  const seen = new Set<string>();

  const visit = (val: any) => {
    if (!val || typeof val !== 'object') return;
    if (Array.isArray(val)) {
      val.forEach(visit);
      return;
    }
    for (const [k, v] of Object.entries(val)) {
      if (k === 'Portfolio' && (typeof v === 'string' || typeof v === 'number')) {
        const s = String(v);
        if (!seen.has(s)) {
          seen.add(s);
          result.push(s);
        }
      } else if (v && typeof v === 'object') {
        visit(v);
      }
    }
  };

  visit(input);
  return result;
};

const supportingDataMatchesSearch = (data: GenevaSupportingData | undefined, query: string): boolean => {
  if (!data || !query.trim()) return false;
  return flattenSupportingSummaries(data).some((summary) => summaryMatchesQuery(summary, query));
};

const getPortfolioDisplayEntries = (data: GenevaSupportingData | undefined, portfolioCodes: string[]): { code: string; name: string | null }[] => {
  const summaries = collectFieldSummaries(data, 'Portfolio');

  if (portfolioCodes.length === 0) {
    return summaries.map((summary) => ({
      code: getDisplayCodeFromSummary(summary) || '',
      name: getDisplayNameFromSummary(summary),
    }));
  }

  return portfolioCodes.map((code) => {
    const matchedSummary = summaries.find((summary) => summaryMatchesCode(summary, code))
      ?? summaries.find((summary) => !summary.code)
      ?? summaries[0];

    return {
      code,
      name: matchedSummary ? getDisplayNameFromSummary(matchedSummary) : null,
    };
  });
};

type RequestSourceName = 'geneva_requests' | 'geneva_bulk_requests';
type GenevaRequestRow = GenevaRequest & { isBulk?: boolean };
type RequestStats = {
  total: number;
  pending: number;
  approved: number;
  completed: number;
};
type RequestSourceState = {
  source: RequestSourceName;
  items: MergedRequestItem[];
  lastDoc?: QueryDocumentSnapshot<DocumentData>;
  exhausted: boolean;
};
type MergedRequestItem = {
  source: RequestSourceName;
  snapshot: QueryDocumentSnapshot<DocumentData>;
  row: GenevaRequestRow;
  createdAtMs: number;
  regularRequest?: GenevaRequest;
  bulkRequest?: BulkGenevaRequest;
};
type ResolveMergedPageParams = {
  db: Firestore;
  page: number;
  rowsPerPage: number;
  filterStatus: string;
  filterType: string;
  searchQuery: string;
};
type ResolveMergedPageResult = {
  pageRows: GenevaRequestRow[];
  loadedRequests: GenevaRequest[];
  loadedBulkRequests: BulkGenevaRequest[];
  totalRows: number;
  listenCounts: Record<RequestSourceName, number>;
};

const EMPTY_STATS: RequestStats = {
  total: 0,
  pending: 0,
  approved: 0,
  completed: 0,
};

const REQUEST_SOURCE_NAMES: RequestSourceName[] = ['geneva_requests', 'geneva_bulk_requests'];

const getIncludedSources = (filterType: string): RequestSourceName[] => {
  if (filterType === 'regular') {
    return ['geneva_requests'];
  }

  if (filterType === 'bulk') {
    return ['geneva_bulk_requests'];
  }

  return REQUEST_SOURCE_NAMES;
};

const toMillis = (value: any): number => {
  if (typeof value === 'number') {
    return value;
  }

  if (value instanceof Date) {
    return value.getTime();
  }

  if (typeof value?.toMillis === 'function') {
    return value.toMillis();
  }

  if (typeof value?.toDate === 'function') {
    return value.toDate().getTime();
  }

  if (typeof value?.seconds === 'number') {
    return value.seconds * 1000;
  }

  return 0;
};

const deserializeRegularRequest = (snapshot: QueryDocumentSnapshot<DocumentData>): GenevaRequest => ({
  id: snapshot.id,
  ...snapshot.data(),
} as GenevaRequest);

const deserializeBulkRequest = (snapshot: QueryDocumentSnapshot<DocumentData>): BulkGenevaRequest => {
  const data = snapshot.data();

  return {
    id: snapshot.id,
    orgId: data.orgId,
    templateId: data.templateId,
    status: data.status,
    isBulk: data.isBulk,
    recordType: data.recordType,
    fieldMappings: data.fieldMappings,
    parsedData: {
      ...data.parsedData,
      rows: typeof data.parsedData?.rows === 'string'
        ? JSON.parse(data.parsedData.rows)
        : data.parsedData?.rows,
    },
    bulkFormData: typeof data.bulkFormData === 'string'
      ? JSON.parse(data.bulkFormData)
      : data.bulkFormData,
    metadata: data.metadata,
    audit: data.audit,
    processing: data.processing,
    geneva_responses: data.geneva_responses,
    schemaType: data.schemaType,
  } as BulkGenevaRequest;
};

const toBulkTableRow = (bulkRequest: BulkGenevaRequest): GenevaRequest & { isBulk: true } => ({
  id: bulkRequest.id,
  orgId: bulkRequest.orgId,
  templateId: bulkRequest.templateId,
  status: bulkRequest.status as GenevaRequest['status'],
  metadata: {
    recordType: bulkRequest.recordType,
    isMultiRecord: false,
    fieldCount: Object.keys(bulkRequest.fieldMappings).length,
    arrayFieldCount: 0,
  },
  formData: { _bulkData: bulkRequest.bulkFormData },
  audit: bulkRequest.audit,
  createdAt: new Timestamp(Math.floor(bulkRequest.audit.createdAt / 1000), 0),
  updatedAt: new Timestamp(Math.floor((bulkRequest.audit.updatedAt || bulkRequest.audit.createdAt) / 1000), 0),
  isBulk: true,
  geneva_response: bulkRequest.geneva_responses,
});

const toMergedRequestItem = (
  source: RequestSourceName,
  snapshot: QueryDocumentSnapshot<DocumentData>,
): MergedRequestItem => {
  const request = source === 'geneva_requests'
    ? deserializeRegularRequest(snapshot)
    : undefined;
  const bulkRequest = source === 'geneva_bulk_requests'
    ? deserializeBulkRequest(snapshot)
    : undefined;
  const row = request ?? toBulkTableRow(bulkRequest!);
  const createdAtMs = toMillis(snapshot.data().createdAt) || row.audit.createdAt;

  return {
    source,
    snapshot,
    row,
    createdAtMs,
    regularRequest: request,
    bulkRequest,
  };
};

const compareMergedItems = (a: MergedRequestItem, b: MergedRequestItem): number => {
  if (a.createdAtMs !== b.createdAtMs) {
    return b.createdAtMs - a.createdAtMs;
  }

  if (a.source !== b.source) {
    return a.source.localeCompare(b.source);
  }

  return b.snapshot.id.localeCompare(a.snapshot.id);
};

const matchesRequestFilters = (request: GenevaRequestRow, filterStatus: string, searchQuery: string): boolean => {
  if (filterStatus !== 'all' && request.status !== filterStatus) {
    return false;
  }

  if (!searchQuery.trim()) {
    return true;
  }

  const normalizedQuery = searchQuery.trim().toLowerCase();
  const matchesBasic =
    request.id.toLowerCase().includes(normalizedQuery) ||
    request.metadata.recordType.toLowerCase().includes(normalizedQuery) ||
    request.audit.createdByEmail.toLowerCase().includes(normalizedQuery) ||
    (request.audit as any).createdByName?.toLowerCase?.().includes(normalizedQuery);

  if (matchesBasic) {
    return true;
  }

  const portfolioMatches = extractPortfolios(request.formData).some((portfolio) =>
    portfolio?.toLowerCase?.().includes(normalizedQuery),
  );

  if (portfolioMatches) {
    return true;
  }

  return supportingDataMatchesSearch(request.supportingData as GenevaSupportingData | undefined, normalizedQuery);
};

const fetchSourceBatch = async (
  db: Firestore,
  state: RequestSourceState,
  batchSize: number,
): Promise<RequestSourceState> => {
  if (state.exhausted) {
    return state;
  }

  const batchQuery = state.lastDoc
    ? query(
      collection(db, state.source),
      orderBy('createdAt', 'desc'),
      startAfter(state.lastDoc),
      limit(batchSize),
    )
    : query(
      collection(db, state.source),
      orderBy('createdAt', 'desc'),
      limit(batchSize),
    );
  const batchSnapshot = await getDocs(batchQuery);

  if (batchSnapshot.empty) {
    return {
      ...state,
      exhausted: true,
    };
  }

  const newItems = batchSnapshot.docs.map((snapshot) => toMergedRequestItem(state.source, snapshot));

  return {
    ...state,
    items: [...state.items, ...newItems],
    lastDoc: batchSnapshot.docs[batchSnapshot.docs.length - 1],
    exhausted: batchSnapshot.docs.length < batchSize,
  };
};

const buildVisibleCountQuery = (
  db: Firestore,
  source: RequestSourceName,
  filterStatus?: string,
) => {
  if (filterStatus && filterStatus !== 'all') {
    return query(
      collection(db, source),
      where('status', '==', filterStatus),
      orderBy('createdAt', 'desc'),
    );
  }

  return query(
    collection(db, source),
    orderBy('createdAt', 'desc'),
  );
};

const loadFilteredRowCount = async (
  db: Firestore,
  sources: RequestSourceName[],
  filterStatus: string,
): Promise<number> => {
  const counts = await Promise.all(
    sources.map(async (source) => {
      const snapshot = await getCountFromServer(buildVisibleCountQuery(db, source, filterStatus));
      return snapshot.data().count;
    }),
  );
  return counts.reduce((sum, count) => sum + count, 0);
};

const resolveMergedPage = async ({
  db,
  page,
  rowsPerPage,
  filterStatus,
  filterType,
  searchQuery,
}: ResolveMergedPageParams): Promise<ResolveMergedPageResult> => {
  const sources = getIncludedSources(filterType);
  const states = new Map<RequestSourceName, RequestSourceState>(
    sources.map((source) => [source, { source, items: [], exhausted: false }]),
  );
  const searchMode = searchQuery.trim().length > 0;
  const targetMatches = page * rowsPerPage;
  const batchSize = Math.max(rowsPerPage, 25);
  const totalRows = searchMode
    ? 0
    : await loadFilteredRowCount(db, sources, filterStatus);

  let filteredRows: MergedRequestItem[] = [];

  while (true) {
    const mergedItems = Array.from(states.values())
      .flatMap((state) => state.items)
      .sort(compareMergedItems);
    filteredRows = mergedItems.filter((item) =>
      matchesRequestFilters(item.row, filterStatus, searchQuery),
    );
    const allExhausted = Array.from(states.values()).every((state) => state.exhausted);

    if (searchMode) {
      if (allExhausted) {
        break;
      }
    } else if (filteredRows.length >= targetMatches) {
      const threshold = filteredRows[targetMatches - 1];
      const canStop = Array.from(states.values()).every((state) => {
        if (state.exhausted) {
          return true;
        }

        const frontier = state.items[state.items.length - 1];
        return !frontier || frontier.createdAtMs <= threshold.createdAtMs;
      });

      if (canStop) {
        break;
      }
    } else if (allExhausted) {
      break;
    }

    const thresholdMs = filteredRows.length >= targetMatches && !searchMode
      ? filteredRows[targetMatches - 1].createdAtMs
      : null;
    const sourcesToFetch = Array.from(states.values())
      .filter((state) => {
        if (state.exhausted) {
          return false;
        }

        if (thresholdMs === null) {
          return true;
        }

        const frontier = state.items[state.items.length - 1];
        return !frontier || frontier.createdAtMs > thresholdMs;
      })
      .map((state) => state.source);

    if (sourcesToFetch.length === 0) {
      break;
    }

    const nextStates = await Promise.all(
      sourcesToFetch.map(async (source) => {
        const state = states.get(source)!;
        return fetchSourceBatch(db, state, batchSize);
      }),
    );

    nextStates.forEach((state) => {
      states.set(state.source, state);
    });
  }

  const startIndex = (page - 1) * rowsPerPage;
  const pageRows = filteredRows.slice(startIndex, startIndex + rowsPerPage).map((item) => item.row);

  return {
    pageRows,
    loadedRequests: (states.get('geneva_requests')?.items || [])
      .map((item) => item.regularRequest)
      .filter((item): item is GenevaRequest => Boolean(item)),
    loadedBulkRequests: (states.get('geneva_bulk_requests')?.items || [])
      .map((item) => item.bulkRequest)
      .filter((item): item is BulkGenevaRequest => Boolean(item)),
    totalRows: searchMode ? filteredRows.length : totalRows,
    listenCounts: {
      geneva_requests: states.get('geneva_requests')?.items.length || 0,
      geneva_bulk_requests: states.get('geneva_bulk_requests')?.items.length || 0,
    },
  };
};

const loadRequestStats = async (db: Firestore): Promise<RequestStats> => {
  const [regularTotal, bulkTotal, regularPending, bulkPending, regularApproved, bulkApproved, regularCompleted, bulkCompleted] = await Promise.all([
    getCountFromServer(buildVisibleCountQuery(db, 'geneva_requests')),
    getCountFromServer(buildVisibleCountQuery(db, 'geneva_bulk_requests')),
    getCountFromServer(buildVisibleCountQuery(db, 'geneva_requests', 'pending')),
    getCountFromServer(buildVisibleCountQuery(db, 'geneva_bulk_requests', 'pending')),
    getCountFromServer(buildVisibleCountQuery(db, 'geneva_requests', 'approved')),
    getCountFromServer(buildVisibleCountQuery(db, 'geneva_bulk_requests', 'approved')),
    getCountFromServer(buildVisibleCountQuery(db, 'geneva_requests', 'completed')),
    getCountFromServer(buildVisibleCountQuery(db, 'geneva_bulk_requests', 'completed')),
  ]);

  return {
    total: regularTotal.data().count + bulkTotal.data().count,
    pending: regularPending.data().count + bulkPending.data().count,
    approved: regularApproved.data().count + bulkApproved.data().count,
    completed: regularCompleted.data().count + bulkCompleted.data().count,
  };
};

const GenevaRequests = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const db = useFirestore();
  const functions = useFunctions();
  const userContext = useContext(MyUserDataContext);

  const customClaims = (userContext.firebaseTokenResult?.claims?.customClaims as CustomClaims) || {};
  const canCreate = Boolean(customClaims.geneva_requests_create);
  const canApprove = Boolean(customClaims.geneva_requests_approve);
  const caConfig = useContext(ConfigContext).lsegCorporateActions;
  const canGetCa = Boolean((customClaims.oper || customClaims.admin) && caConfig?.manualEnabled);
  const [caModalOpen, setCaModalOpen] = useState(false);

  const showPermissionDenied = (message: string) => {
    notifications.show({
      title: 'Permission denied',
      message,
      color: 'red',
    });
  };

  const requireCreatePermission = (actionDescription: string): boolean => {
    if (canCreate) {
      return true;
    }
    showPermissionDenied(`You do not have permission to ${actionDescription}.`);
    return false;
  };

  const requireApprovePermission = (actionDescription: string): boolean => {
    if (canApprove) {
      return true;
    }
    showPermissionDenied(`You do not have permission to ${actionDescription}.`);
    return false;
  };

  const orgId = searchParams.get('orgId') || 'demo';
  const schemaTypeParam = searchParams.get('schemaType');
  const defaultTemplateId = schemaTypeParam === 'gwi' ? 'gwi' : 'default';
  const templateId = searchParams.get('templateId') || defaultTemplateId;

  const [requests, setRequests] = useState<GenevaRequest[]>([]);
  const [bulkRequests, setBulkRequests] = useState<BulkGenevaRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(50);
  const [tableRows, setTableRows] = useState<GenevaRequestRow[]>([]);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [stats, setStats] = useState<RequestStats>(EMPTY_STATS);
  const [selectedRequest, setSelectedRequest] = useState<GenevaRequest | null>(null);
  const [selectedBulkRequest, setSelectedBulkRequest] = useState<BulkGenevaRequest | null>(null);
  const [detailsModal, setDetailsModal] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [approvingRequest, setApprovingRequest] = useState(false);
  const [undoingRequest, setUndoingRequest] = useState<string | null>(null);
  const [responseOpen, setResponseOpen] = useState(false);
  const [bulkResponseOpen, setBulkResponseOpen] = useState(false);
  const [lastGenevaError, setLastGenevaError] = useState<GenevaLoaderError | null>(null);

  const downloadXmlFile = (xmlContent: string, filename: string) => {
    const blob = new Blob([xmlContent], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  // Create callable function for XML generation
  const generateXml = httpsCallable<GenerateXmlRequest, BaseResult>(functions, 'generateGenevaXml');

  const showGenevaXmlGenerationError = (error: unknown, requestId: string) => {
    const genevaError = normalizeGenevaXmlError(error, requestId);

    notifications.update({
      id: 'xml-generation',
      title: 'XML Generation Failed',
      message: genevaError.message,
      color: 'red',
      icon: <IconAlertTriangle size={16} />,
      loading: false,
      autoClose: false,
    });

    setLastGenevaError(genevaError);

    if (genevaError.xmlContent) {
      console.error('Generated Geneva XML (error context):', genevaError.xmlContent);
    }
    if (genevaError.rawResponse) {
      console.error('Geneva SOAP fault response:', genevaError.rawResponse);
    }
  };

  useEffect(() => {
    if (!db) return;

    let cancelled = false;

    const fetchStats = async () => {
      try {
        const nextStats = await loadRequestStats(db);
        if (!cancelled) {
          setStats(nextStats);
        }
      } catch (error) {
        console.error('Error loading Geneva request stats:', error);
      }
    };

    void fetchStats();

    return () => {
      cancelled = true;
    };
  }, [db]);

  useEffect(() => {
    setPage(1);
  }, [filterStatus, filterType, searchQuery, rowsPerPage]);

  useEffect(() => {
    if (!db) return;

    let cancelled = false;
    let loadSequence = 0;
    let listenCounts: Record<RequestSourceName, number> = {
      geneva_requests: 0,
      geneva_bulk_requests: 0,
    };
    let unsubscribers: Unsubscribe[] = [];

    const unsubscribeAll = () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
      unsubscribers = [];
    };

    const ensureSubscriptions = (nextCounts: Record<RequestSourceName, number>) => {
      const changed = REQUEST_SOURCE_NAMES.some((source) => listenCounts[source] !== nextCounts[source]);
      if (!changed) {
        return;
      }

      listenCounts = nextCounts;
      unsubscribeAll();

      const sources = getIncludedSources(filterType);
      unsubscribers = sources
        .filter((source) => nextCounts[source] > 0)
        .map((source) =>
          onSnapshot(
            query(
              collection(db, source),
              orderBy('createdAt', 'desc'),
              limit(nextCounts[source]),
            ),
            () => {
              void loadPageData(false);
            },
            (error) => {
              console.error(`Error listening for ${source}:`, error);
            },
          ),
        );
    };

    const loadPageData = async (showLoadingOverlay: boolean) => {
      const requestId = loadSequence + 1;
      loadSequence = requestId;

      if (showLoadingOverlay) {
        setLoading(true);
      }

      try {
        const result = await resolveMergedPage({
          db,
          page,
          rowsPerPage,
          filterStatus,
          filterType,
          searchQuery,
        });

        if (cancelled || requestId !== loadSequence) {
          return;
        }

        setRequests(result.loadedRequests);
        setBulkRequests(result.loadedBulkRequests);
        setTableRows(result.pageRows);
        setTotalRows(result.totalRows);
        ensureSubscriptions(result.listenCounts);
      } catch (error) {
        console.error('Error loading Geneva requests page:', error);
        if (!cancelled && requestId === loadSequence) {
          setRequests([]);
          setBulkRequests([]);
          setTableRows([]);
          setTotalRows(0);
          notifications.show({
            title: 'Error',
            message: 'Failed to load Geneva requests',
            color: 'red',
          });
        }
      } finally {
        if (showLoadingOverlay && !cancelled && requestId === loadSequence) {
          setLoading(false);
        }
      }
    };

    void loadPageData(true);

    return () => {
      cancelled = true;
      unsubscribeAll();
    };
  }, [db, filterStatus, filterType, page, rowsPerPage, searchQuery]);

  const handleCreateNew = () => {
    if (!requireCreatePermission('create Geneva requests')) {
      return;
    }
    const schemaQuery = schemaTypeParam ? `&schemaType=${schemaTypeParam}` : '';
    navigate(`/geneva_requests/create?orgId=${orgId}&templateId=${templateId}${schemaQuery}`);
  };

  const handleCreateBulk = () => {
    if (!requireCreatePermission('create Geneva requests')) {
      return;
    }
    const schemaQuery = schemaTypeParam ? `&schemaType=${schemaTypeParam}` : '';
    navigate(`/geneva_requests/bulk-create?orgId=${orgId}&templateId=${templateId}${schemaQuery}`);
  };

  const handleViewRequest = (request: GenevaRequest) => {
    setSelectedRequest(request);
    setSelectedBulkRequest(null);
    setDetailsModal(true);
  };

  const handleViewBulkRequest = (bulkRequest: BulkGenevaRequest) => {
    setSelectedBulkRequest(bulkRequest);
    setSelectedRequest(null);
    setDetailsModal(true);
  };

  const handleConfigureSchema = () => {
    if (!requireCreatePermission('configure the Geneva schema')) {
      return;
    }
    navigate('/settings?tab=geneva_schema');
  };

  const handleApproveRequest = async () => {
    if (!requireApprovePermission('approve Geneva requests')) {
      return;
    }
    if (!selectedRequest || !userContext.user || approvingRequest) return;

    // Kuwait: a same-day cash dividend is entitled on pre-bonus shares, so it must reach Geneva before the split.
    if (selectedRequest.lsegCa?.type === 'Split') {
      const { investment, exDate } = selectedRequest.lsegCa;
      const pendingDividend = await getDocs(query(collection(db, 'geneva_requests'),
        where('lsegCa.key', '==', `Dividend_${investment}_${exDate}`), where('status', '==', 'pending'), limit(1)));
      if (!pendingDividend.empty) {
        notifications.show({
          title: 'Approve the dividend first',
          message: `The cash dividend for ${investment} on ${exDate} (request ${pendingDividend.docs[0].id}) is still pending.`,
          color: 'orange',
        });
        return;
      }
    }

    setApprovingRequest(true);
    try {
      // Automatically generate XML after approval
      try {
        notifications.show({
          id: 'xml-generation',
          title: 'Generating XML',
          message: `Generating XML file for request ${selectedRequest.id}...`,
          color: 'blue',
          loading: true,
          autoClose: false,
        });

        const result = await generateXml({ request: selectedRequest, orgId, isApproval: true });

        notifications.update({
          id: 'xml-generation',
          title: 'XML Generated',
          message: `XML file generated successfully for request ${selectedRequest.id}`,
          color: 'green',
          icon: <IconCheck size={16} />,
          loading: false,
          autoClose: 3000,
        });

        console.log('XML generation result:', result);
        setLastGenevaError(null);


        const requestRef = doc(db, 'geneva_requests', selectedRequest.id);
        await updateDoc(requestRef, {
          status: 'approved',
          'audit.approvedAt': Date.now(),
          'audit.approvedBy': userContext.user.uid,
          'audit.approvedByEmail': userContext.user.email,
          'audit.approvedByName': userContext.user.displayName,
          'audit.updatedAt': Date.now(),
          'audit.updatedBy': userContext.user.uid,
          'audit.updatedByEmail': userContext.user.email,
          'audit.updatedByName': userContext.user.displayName,
          updatedAt: Timestamp.now(),
        });

        // Show success notification
        notifications.show({
          title: 'Request Approved',
          message: `Request ${selectedRequest.id} has been approved`,
          color: 'green',
          icon: <IconCheck size={16} />,
        });

      } catch (xmlError) {
        console.error('Error generating XML:', xmlError);
        showGenevaXmlGenerationError(xmlError, selectedRequest.id);
      }
      setDetailsModal(false);
      setSelectedRequest(null);
    } catch (error) {
      console.error('Error approving request:', error);
      notifications.show({
        title: 'Approval Failed',
        message: 'Failed to approve request',
        color: 'red',
      });
    } finally {
      setApprovingRequest(false);
    }
  };

  const handleDeleteRequest = async () => {
    if (!requireCreatePermission('delete Geneva requests')) {
      setDeleteModalOpen(false);
      return;
    }
    if ((!selectedRequest && !selectedBulkRequest) || !userContext.user) return;

    try {
      const requestId = selectedRequest?.id || selectedBulkRequest?.id || '';
      const collectionName = selectedBulkRequest ? 'geneva_bulk_requests' : 'geneva_requests';
      const currentStatus = selectedRequest?.status || (selectedBulkRequest as any)?.status;

      if (currentStatus === 'approved') {
        notifications.show({
          title: 'Cannot Delete Approved Request',
          message: 'Approved requests cannot be deleted. Undo first if needed.',
          color: 'orange',
        });
        return;
      }

      await deleteDoc(doc(db, collectionName, requestId));

      notifications.show({
        title: `${selectedBulkRequest ? 'Bulk Request' : 'Request'} Deleted`,
        message: `${selectedBulkRequest ? 'Bulk request' : 'Request'} ${requestId} has been deleted`,
        color: 'green',
        icon: <IconCheck size={16} />,
      });

      setDeleteModalOpen(false);
      setDetailsModal(false);
      setSelectedRequest(null);
      setSelectedBulkRequest(null);
    } catch (error) {
      console.error('Error deleting request:', error);
      notifications.show({
        title: 'Delete Failed',
        message: 'Failed to delete request',
        color: 'red',
      });
    }
  };

  // Generate and download XML for a specific request (used from table actions)
  // Handle undo request (generate XML with Delete loader action)
  const handleUndoRequest = async (request: GenevaRequest & { isBulk?: boolean }) => {
    if (!request) {
      notifications.show({
        title: 'Error',
        message: 'No request data available',
        color: 'red',
      });
      return;
    }

    if (!requireCreatePermission('undo Geneva requests')) {
      return;
    }

    setUndoingRequest(request.id);

    try {
      // Show loading notification
      notifications.show({
        id: 'undo-generation',
        title: 'Generating Undo XML',
        message: `Generating undo XML for request ${request.id}...`,
        color: 'orange',
        loading: true,
        autoClose: false,
      });

      // Prepare the request data for the Firebase function
      let requestForFunction: any = request;

      // If it's a bulk request, we need to restructure the data
      if (request.isBulk) {
        const bulkRequest = bulkRequests.find(br => br.id === request.id);
        if (bulkRequest) {
          requestForFunction = bulkRequest;
        }
      }

      // Call the Firebase function to generate undo XML
      const result = await generateXml({
        request: requestForFunction,
        orgId,
        isUndo: true,
        isApproval: true // Send to Geneva immediately
      });

      // Update the request status back to pending in Firestore
      try {
        const collection = request.isBulk ? 'geneva_bulk_requests' : 'geneva_requests';
        const requestRef = doc(db, collection, request.id);
        await updateDoc(requestRef, {
          status: 'pending',
          'audit.updatedAt': Date.now(),
          'audit.updatedBy': userContext.user?.uid,
          'audit.updatedByEmail': userContext.user?.email,
          'audit.updatedByName': userContext.user?.displayName,
          updatedAt: Timestamp.now(),
        });
      } catch (statusError) {
        console.error('Error updating request status:', statusError);
        // Don't fail the whole operation for this
      }

      // Update notification
      notifications.update({
        id: 'undo-generation',
        title: 'Request Undone',
        message: `Request ${request.id} has been undone and set back to pending`,
        color: 'green',
        icon: <IconCheck size={16} />,
        loading: false,
        autoClose: 3000,
      });

      console.log('Undo XML generation result:', result);
    } catch (error) {
      console.error('Error generating undo XML:', error);
      notifications.update({
        id: 'undo-generation',
        title: 'Undo Failed',
        message: error instanceof Error ? error.message : 'Failed to generate undo XML',
        color: 'red',
        loading: false,
        autoClose: 5000,
      });
    } finally {
      setUndoingRequest(null);
    }
  };

  const handleGenerateXmlObjectForRequest = async (request: GenevaRequest & { isBulk?: boolean }) => {
    if (!request) {
      notifications.show({
        title: 'Error',
        message: 'No request data available',
        color: 'red',
      });
      return;
    }

    if (!requireCreatePermission('generate Geneva XML files')) {
      return;
    }

    try {
      // Show loading notification
      notifications.show({
        id: 'xml-generation',
        title: 'Generating XML',
        message: `Generating XML file for request ${request.id}...`,
        color: 'blue',
        loading: true,
        autoClose: false,
      });

      // Prepare the request data for the Firebase function
      let requestForFunction: any = request;

      // If it's a bulk request, we need to restructure the data
      if (request.isBulk) {
        const bulkRequest = bulkRequests.find(br => br.id === request.id);
        if (bulkRequest) {
          requestForFunction = bulkRequest;
        }
      }

      // Call the Firebase function to generate XML
      const result = await generateXml({ request: requestForFunction, orgId });

      if (result.data?.data?.xmlContent) {
        const xmlContent = result.data.data.xmlContent;
        downloadXmlFile(xmlContent, `geneva-${request.isBulk ? 'bulk-' : ''}request-${request.id}.xml`);

        notifications.update({
          id: 'xml-generation',
          title: 'XML Downloaded',
          message: `XML file for request ${request.id} has been downloaded`,
          color: 'green',
          icon: <IconCheck size={16} />,
          loading: false,
          autoClose: 3000,
        });

        console.log('XML generated and downloaded successfully');
        setLastGenevaError(null);
      } else {
        throw new Error('No XML content in response');
      }
    } catch (error) {
      console.error('Error generating XML:', error);
      showGenevaXmlGenerationError(error, request.id);
    }
  };

  // Generate XML object for the selected request (used from modal)
  const handleGenerateXmlObject = async () => {
    if (selectedRequest) {
      await handleGenerateXmlObjectForRequest(selectedRequest);
    } else if (selectedBulkRequest) {
      // For bulk requests, pass a simple object with the id and isBulk flag
      // The handleGenerateXmlObjectForRequest will fetch the actual bulk request data
      await handleGenerateXmlObjectForRequest({
        id: selectedBulkRequest.id,
        isBulk: true,
      } as any);
    } else {
      notifications.show({
        title: 'Error',
        message: 'No request selected',
        color: 'red',
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'draft': return 'gray';
      case 'pending': return 'blue';
      case 'approved': return 'green';
      case 'processing': return 'blue';
      case 'completed': return 'teal';
      case 'error': return 'red';
      default: return 'gray';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'draft': return <IconFileText size={16} />;
      case 'pending': return <IconClock size={16} />;
      case 'approved': return <IconCheck size={16} />;
      case 'processing': return <IconClock size={16} />;
      case 'completed': return <IconCheck size={16} />;
      case 'error': return <IconX size={16} />;
      default: return <IconFileText size={16} />;
    }
  };

  const getGenevaStatusBadge = (genevaResponse?: GenevaResponse | Record<string, GenevaResponse> | Record<string, Record<string, GenevaResponse>>) => {
    if (!genevaResponse) {
      return null;
    }

    // Handle bulk multi-record responses (Record<string, Record<string, GenevaResponse>>) 
    // Structure: { "0": { "SpotFX": {...}, "Transfer": {...} }, "1": { "SpotFX": {...}, "Transfer": {...} } }
    if (typeof genevaResponse === 'object' && !('status' in genevaResponse)) {
      const firstKey = Object.keys(genevaResponse)[0];
      const firstValue = genevaResponse[firstKey];

      // Check if this is bulk multi-record format (indexed by row number)
      if (firstValue && typeof firstValue === 'object' && !('status' in firstValue)) {
        // This is bulk multi-record: flatten all responses
        const allResponses: GenevaResponse[] = [];
        Object.values(genevaResponse as Record<string, Record<string, GenevaResponse>>).forEach(rowResponses => {
          Object.values(rowResponses).forEach(response => {
            if (response && 'status' in response) {
              allResponses.push(response);
            }
          });
        });

        const successCount = allResponses.filter(r => r.status?.toLowerCase() === 'success').length;
        const failureCount = allResponses.filter(r => r.status?.toLowerCase() === 'failure').length;
        const total = allResponses.length;

        if (total === 0) return null;

        if (failureCount === 0) {
          // All successful
          return (
            <Badge
              size="sm"
              variant="light"
              color="green"
              leftSection={<IconCircleCheck size={12} />}
            >
              {successCount}/{total} ✓
            </Badge>
          );
        } else if (successCount === 0) {
          // All failed
          return (
            <Badge
              size="sm"
              variant="light"
              color="red"
              leftSection={<IconX size={12} />}
            >
              {failureCount}/{total} ✗
            </Badge>
          );
        } else {
          // Mixed
          return (
            <Badge
              size="sm"
              variant="light"
              color="yellow"
              leftSection={<IconAlertTriangle size={12} />}
            >
              {successCount}/{total} ⚠
            </Badge>
          );
        }
      } else {
        // Handle regular bulk responses (Record<string, GenevaResponse>) - single record per request
        const responses = Object.values(genevaResponse as Record<string, GenevaResponse>);
        const successCount = responses.filter(r => r.status?.toLowerCase() === 'success').length;
        const failureCount = responses.filter(r => r.status?.toLowerCase() === 'failure').length;
        const total = responses.length;

        if (total === 0) return null;

        if (failureCount === 0) {
          // All successful
          return (
            <Badge
              size="sm"
              variant="light"
              color="green"
              leftSection={<IconCircleCheck size={12} />}
            >
              {successCount}/{total} ✓
            </Badge>
          );
        } else if (successCount === 0) {
          // All failed
          return (
            <Badge
              size="sm"
              variant="light"
              color="red"
              leftSection={<IconX size={12} />}
            >
              {failureCount}/{total} ✗
            </Badge>
          );
        } else {
          // Mixed
          return (
            <Badge
              size="sm"
              variant="light"
              color="yellow"
              leftSection={<IconAlertTriangle size={12} />}
            >
              {successCount}/{total} ⚠
            </Badge>
          );
        }
      }
    }

    // Handle single response
    const response = genevaResponse as GenevaResponse;

    // Check if response is empty or has no status
    if (!response.status || response.status.trim() === '') {
      return null;
    }

    const isSuccess = response.status?.toLowerCase() === 'success';

    return (
      <Badge
        size="sm"
        variant="light"
        color={isSuccess ? 'green' : 'red'}
        leftSection={isSuccess ? <IconCircleCheck size={12} /> : <IconX size={12} />}
      >
        {isSuccess ? '✓' : '✗'}
      </Badge>
    );
  };

  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));

  useEffect(() => {
    setPage((currentPage) => Math.min(currentPage, totalPages));
  }, [totalPages]);

  const visibleRangeStart = totalRows === 0 ? 0 : (page - 1) * rowsPerPage + 1;
  const visibleRangeEnd = totalRows === 0
    ? 0
    : Math.min(page * rowsPerPage, totalRows);

  return (
    <Container fluid px="md" py="md">
      {canGetCa && (
        <LsegCorporateActionsModal opened={caModalOpen} onClose={() => setCaModalOpen(false)} config={caConfig!} />
      )}

      {/* Header */}
      <Group justify="space-between" mb="xl">
        <Box>
          <Title order={1}>Geneva Requests</Title>
        </Box>

        {(canCreate || customClaims.geneva_requests_approve || canGetCa) && (
          <Group>
            {canGetCa && (
              <Button
                variant="outline"
                color="grape"
                leftSection={<IconCloudDownload size={16} />}
                onClick={() => setCaModalOpen(true)}
              >
                Get CA
              </Button>
            )}
            {customClaims.geneva_requests_approve && (
              <Button
                variant="outline"
                color="teal"
                leftSection={<IconCalendarRepeat size={16} />}
                onClick={() => navigate('/geneva_requests/expense_managers')}
              >
                Expense Manager
              </Button>
            )}
            {canCreate && (<>
            <Button
              variant="light"
              leftSection={<IconSettings size={16} />}
              onClick={handleConfigureSchema}
            >
              Configure Schema
            </Button>
            <Button
              variant="outline"
              leftSection={<IconFileSpreadsheet size={16} />}
              onClick={handleCreateBulk}
            >
              Bulk Create
            </Button>
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={handleCreateNew}
            >
              Create New Request
            </Button>
            </>)}
          </Group>
        )}
      </Group>

      {lastGenevaError && (
        <Alert
          color="red"
          icon={<IconAlertTriangle size={16} />}
          title="Geneva Loader Error"
          mb="md"
          withCloseButton
          onClose={() => setLastGenevaError(null)}
        >
          <Stack gap="xs">
            <Text size="sm">{lastGenevaError.message}</Text>
            <Group gap="xs" wrap="wrap">
              {lastGenevaError.statusCode !== undefined && (
                <Badge size="xs" color="red" variant="light">
                  HTTP {lastGenevaError.statusCode}
                </Badge>
              )}
              {lastGenevaError.code && (
                <Badge size="xs" color="red" variant="outline">
                  Code: {lastGenevaError.code}
                </Badge>
              )}
              {lastGenevaError.faultCode && (
                <Badge size="xs" color="red" variant="outline">
                  Fault Code: {lastGenevaError.faultCode}
                </Badge>
              )}
              {lastGenevaError.operation && (
                <Badge size="xs" color="gray" variant="outline">
                  Operation: {lastGenevaError.operation}
                </Badge>
              )}
              {lastGenevaError.requestId && (
                <Badge size="xs" color="gray" variant="outline">
                  Request: {lastGenevaError.requestId}
                </Badge>
              )}
            </Group>
            {lastGenevaError.faultString && (
              <Text size="sm" c="dimmed">
                {lastGenevaError.faultString}
              </Text>
            )}
            {lastGenevaError.rawResponse && (
              <Box>
                <Text size="xs" c="dimmed">
                  SOAP Fault Envelope
                </Text>
                <ScrollArea h={140} mt={4} type="auto">
                  <Code block>
                    {lastGenevaError.rawResponse}
                  </Code>
                </ScrollArea>
              </Box>
            )}
            <Group gap="sm">
              {lastGenevaError.xmlContent && (
                <Button
                  size="xs"
                  variant="light"
                  leftSection={<IconDownload size={14} />}
                  onClick={() => downloadXmlFile(
                    lastGenevaError.xmlContent!,
                    `geneva-error-${lastGenevaError.requestId ?? 'request'}.xml`,
                  )}
                >
                  Download XML
                </Button>
              )}
            </Group>
          </Stack>
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid mb="xl">
        <Grid.Col span={3}>
          <Card withBorder>
            <Group justify="space-between">
              <Box>
                <Text size="xs" tt="uppercase" c="dimmed" fw={700}>
                  Total Requests
                </Text>
                <Text fw={700} size="xl">
                  {stats.total}
                </Text>
              </Box>
              <ThemeIcon size={38} radius="md" variant="light">
                <IconFileText size={24} />
              </ThemeIcon>
            </Group>
          </Card>
        </Grid.Col>

        <Grid.Col span={3}>
          <Card withBorder>
            <Group justify="space-between">
              <Box>
                <Text size="xs" tt="uppercase" c="dimmed" fw={700}>
                  Pending
                </Text>
                <Text fw={700} size="xl">
                  {stats.pending}
                </Text>
              </Box>
              <ThemeIcon size={38} radius="md" variant="light" color="blue">
                <IconClock size={24} />
              </ThemeIcon>
            </Group>
          </Card>
        </Grid.Col>

        <Grid.Col span={3}>
          <Card withBorder>
            <Group justify="space-between">
              <Box>
                <Text size="xs" tt="uppercase" c="dimmed" fw={700}>
                  Approved
                </Text>
                <Text fw={700} size="xl">
                  {stats.approved}
                </Text>
              </Box>
              <ThemeIcon size={38} radius="md" variant="light" color="green">
                <IconCheck size={24} />
              </ThemeIcon>
            </Group>
          </Card>
        </Grid.Col>

        <Grid.Col span={3}>
          <Card withBorder>
            <Group justify="space-between">
              <Box>
                <Text size="xs" tt="uppercase" c="dimmed" fw={700}>
                  Completed
                </Text>
                <Text fw={700} size="xl">
                  {stats.completed}
                </Text>
              </Box>
              <ThemeIcon size={38} radius="md" variant="light" color="teal">
                <IconCheck size={24} />
              </ThemeIcon>
            </Group>
          </Card>
        </Grid.Col>
      </Grid>

      {/* Filters */}
      <Card withBorder mb="md">
        <Group>
          <TextInput
            placeholder="Search requests..."
            leftSection={<IconSearch size={16} />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ flex: 1 }}
          />
          <Select
            placeholder="Filter by status"
            leftSection={<IconFilter size={16} />}
            value={filterStatus}
            onChange={(value) => setFilterStatus(value || 'all')}
            data={[
              { value: 'all', label: 'All Statuses' },
              { value: 'draft', label: 'Draft' },
              { value: 'pending', label: 'Pending' },
              { value: 'approved', label: 'Approved' },
              { value: 'processing', label: 'Processing' },
              { value: 'completed', label: 'Completed' },
              { value: 'error', label: 'Error' },
            ]}
            w={160}
          />
          <Select
            placeholder="Rows per page"
            value={String(rowsPerPage)}
            onChange={(value) => setRowsPerPage(Number(value) || 50)}
            data={[
              { value: '25', label: '25 / page' },
              { value: '50', label: '50 / page' },
              { value: '100', label: '100 / page' },
              { value: '250', label: '250 / page' },
            ]}
            w={140}
          />
        </Group>
      </Card>

      {/* Requests Table */}
      <Card withBorder>
        <Box pos="relative">
          <LoadingOverlay visible={loading} />
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Portfolio</Table.Th>
                <Table.Th>Name</Table.Th>
                <Table.Th>TRX Type</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Geneva Status</Table.Th>
                <Table.Th>Created By</Table.Th>
                <Table.Th>Created</Table.Th>
                <Table.Th>Approved</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {tableRows.map((request) => (
                <Table.Tr key={request.id}>

                  {(() => {
                    const portfolios = extractPortfolios(request.formData);
                    const supporting = request.supportingData as GenevaSupportingData | undefined;
                    let entries = getPortfolioDisplayEntries(supporting, portfolios);

                    if (entries.length === 0) {
                      if (portfolios.length === 0) {
                        const fallbackSummaries = collectFieldSummaries(supporting, 'Portfolio');
                        entries = fallbackSummaries.map((summary) => ({
                          code: getDisplayCodeFromSummary(summary) || '',
                          name: getDisplayNameFromSummary(summary),
                        }));
                      } else {
                        entries = portfolios.map((code) => ({ code, name: null }));
                      }
                    }

                    if (entries.length === 0) {
                      return (<><Table.Td>
                        <Text size="sm" c="dimmed">—</Text>
                      </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed">—</Text>
                        </Table.Td>
                      </>
                      );
                    }

                    if (entries.length === 1) {
                      const entry = entries[0];
                      const primary = entry.code || entry.name || '—';
                      return (<>
                        <Table.Td>
                          <Text fw={500} size="sm">{primary}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text fw={500} size="sm">{entry.name || '—'}</Text>
                        </Table.Td>
                      </>);
                    }

                    return (<>
                      <Table.Td>
                        <Text c="dimmed" fw={500} size="sm">Multiple</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text c="dimmed" fw={500} size="sm">—</Text>
                      </Table.Td>
                    </>
                    );
                  })()}


                  <Table.Td>
                    <Group gap="xs">
                      {request.metadata.isMultiRecord ? (
                        // For multi-records, show the virtual record label
                        <Text size="sm">{request.metadata.recordType}</Text>
                      ) : (
                        <Text size="sm">{request.metadata.recordType}</Text>
                      )}
                      {/* {request.metadata.isMultiRecord && (
                        <Badge size="xs" variant="outline" color="violet">
                          Multi-Record
                        </Badge>
                      )} */}
                      {request.isBulk && request.formData._bulkData && (
                        <Badge size="xs" variant="outline" color="blue">
                          {Array.isArray(request.formData._bulkData) ? request.formData._bulkData.length : 0} records
                        </Badge>
                      )}
                      {/* {request.formData._multiRecord && request.formData.records && (
                        <Badge size="xs" variant="outline" color="grape">
                          {Object.keys(request.formData.records).length} transactions
                        </Badge>
                      )} */}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      leftSection={getStatusIcon(request.status)}
                      color={getStatusColor(request.status)}
                      variant="light"
                    >
                      {request.status}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    {getGenevaStatusBadge(request.geneva_responses ? request.geneva_responses : request.geneva_response)}
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{(request.audit as any).createdByName || request.audit.createdByEmail}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">
                      {request.createdAt?.toDate?.()?.toLocaleDateString() ||
                        new Date(request.audit.createdAt).toLocaleDateString()}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">
                      {request.audit.approvedAt ? new Date(request.audit.approvedAt).toLocaleDateString() : '-'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      <Tooltip label="View Details">
                        <ActionIcon
                          variant="subtle"
                          onClick={() => {
                            if (request.isBulk) {
                              const bulkRequest = bulkRequests.find(br => br.id === request.id);
                              if (bulkRequest) handleViewBulkRequest(bulkRequest);
                            } else {
                              handleViewRequest(request);
                            }
                          }}
                        >
                          <IconEye size={16} />
                        </ActionIcon>
                      </Tooltip>
                      {canCreate && !request.isBulk && request.status !== 'approved' && (
                        <Tooltip label="Edit Request">
                          <ActionIcon
                            variant="subtle"
                            onClick={() => {
                              if (!requireCreatePermission('edit Geneva requests')) {
                                return;
                              }
                              const schemaQuery = schemaTypeParam ? `&schemaType=${schemaTypeParam}` : '';
                              navigate(`/geneva_requests/${request.id}?orgId=${orgId}&templateId=${templateId}${schemaQuery}&tab=edit`);
                            }}
                          >
                            <IconEdit size={16} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                      {canCreate && request.status !== 'approved' && (
                        <Tooltip label="Delete Request">
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            onClick={() => {
                              if (!requireCreatePermission('delete Geneva requests')) {
                                return;
                              }
                              if (request.isBulk) {
                                const bulkRequest = bulkRequests.find(br => br.id === request.id);
                                if (bulkRequest) {
                                  setSelectedBulkRequest(bulkRequest);
                                  setSelectedRequest(null);
                                  setDeleteModalOpen(true);
                                }
                              } else {
                                setSelectedRequest(request);
                                setSelectedBulkRequest(null);
                                setDeleteModalOpen(true);
                              }
                            }}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>

          {tableRows.length === 0 && !loading && (
            <Box ta="center" py="xl">
              <Text c="dimmed">No requests found</Text>
              {canCreate ? (
                <Button mt="md" onClick={handleCreateNew}>
                  Create your first request
                </Button>
              ) : (
                <Text mt="sm" size="sm" c="dimmed">
                  You do not have permission to create Geneva requests.
                </Text>
              )}
            </Box>
          )}

          {tableRows.length > 0 && (
            <Group justify="space-between" mt="md">
              <Text size="sm" c="dimmed">
                Showing {visibleRangeStart}-{visibleRangeEnd} of {totalRows} requests
              </Text>
              <Pagination
                total={totalPages}
                value={page}
                onChange={setPage}
              />
            </Group>
          )}
        </Box>
      </Card>

      {/* Details Modal */}
      <Modal
        opened={detailsModal}
        onClose={() => {
          setDetailsModal(false);
          setSelectedRequest(null);
          setSelectedBulkRequest(null);
        }}
        title={selectedBulkRequest ? "Bulk Request Details" : "Request Details"}
        size="xl"
      >
        {(() => {
          // Get fresh data from the arrays to reflect real-time updates
          const currentRequest = selectedRequest
            ? requests.find(r => r.id === selectedRequest.id) || selectedRequest
            : null;
          const currentBulkRequest = selectedBulkRequest
            ? bulkRequests.find(br => br.id === selectedBulkRequest.id) || selectedBulkRequest
            : null;

          if (currentRequest) {
            const supportingRecords = normalizeSupportingRecords(currentRequest.supportingData as GenevaSupportingData | undefined);
            const fallbackSupport = Object.values(supportingRecords)[0];
            const approveDisabledReasons: string[] = [];
            if (!canApprove) {
              approveDisabledReasons.push('You do not have permission to approve Geneva requests.');
            }
            if (currentRequest.status !== 'pending') {
              approveDisabledReasons.push(`Only pending requests can be approved (current status: ${currentRequest.status}).`);
            }
            const approveDisabledReason = approveDisabledReasons.length ? approveDisabledReasons.join(' ') : null;
            const approveDisabled = Boolean(approveDisabledReason) || approvingRequest;

            const editDisabledReasons: string[] = [];
            if (!canCreate) {
              editDisabledReasons.push('You do not have permission to edit Geneva requests.');
            }
            if (currentRequest.status === 'approved') {
              editDisabledReasons.push('Approved requests cannot be edited.');
            }
            const editDisabledReason = editDisabledReasons.length ? editDisabledReasons.join(' ') : null;
            const editDisabled = Boolean(editDisabledReason);

            const deleteDisabledReasons: string[] = [];
            if (!canCreate) {
              deleteDisabledReasons.push('You do not have permission to delete Geneva requests.');
            }
            if (currentRequest.status === 'approved') {
              deleteDisabledReasons.push('Approved requests cannot be deleted.');
            }
            const deleteDisabledReason = deleteDisabledReasons.length ? deleteDisabledReasons.join(' ') : null;
            const deleteDisabled = Boolean(deleteDisabledReason);

            const generateDisabledReason = canCreate ? null : 'You do not have permission to create Geneva requests.';
            const generateDisabled = Boolean(generateDisabledReason);

            const undoDisabledReasons: string[] = [];
            if (!canCreate) {
              undoDisabledReasons.push('You do not have permission to undo Geneva requests.');
            }
            const undoDisabledReason = undoDisabledReasons.length ? undoDisabledReasons.join(' ') : null;
            const undoDisabled = Boolean(undoDisabledReason) || (undoingRequest === currentRequest.id);
            return (
              <Stack gap="md">
                {/* Request Info */}
                <Card withBorder>
                  <Stack gap="xs">
                    <Group justify="space-between">
                      <Text fw={500}>Request ID:</Text>
                      <Code>{currentRequest.id}</Code>
                    </Group>
                    <Group justify="space-between">
                      <Text fw={500}>TRX Type:</Text>
                      <Badge>{currentRequest.metadata.recordType}</Badge>
                    </Group>
                    <Group justify="space-between">
                      <Text fw={500}>Status:</Text>
                      <Badge
                        leftSection={getStatusIcon(currentRequest.status)}
                        color={getStatusColor(currentRequest.status)}
                      >
                        {currentRequest.status}
                      </Badge>
                    </Group>
                    {(currentRequest.geneva_response || currentRequest.geneva_responses) && (
                      <Group justify="space-between">
                        <Text fw={500}>Geneva Status:</Text>
                        {getGenevaStatusBadge(currentRequest.geneva_responses ? currentRequest.geneva_responses : currentRequest.geneva_response)}
                      </Group>
                    )}
                  </Stack>
                </Card>

                {/* Geneva Response was moved to bottom as collapsible */}
                {/* Form Data */}
                <Card withBorder>
                  <Text fw={600} mb="sm">Form Data</Text>
                  <ScrollArea h={300}>
                    {currentRequest.formData._multiRecord && currentRequest.formData.records ? (
                      <Stack gap="md">
                        <Badge color="violet" variant="light" size="sm">Multi-Record Request</Badge>
                        {Object.entries(currentRequest.formData.records).map(([recordKey, recordData]) => {
                          const recordName = recordKey.split('__')[1] || recordKey;
                          const supportingForRecord = supportingRecords[recordKey]
                            || supportingRecords[recordName]
                            || fallbackSupport;
                          return (
                            <Card key={recordKey} withBorder>
                              <Card.Section withBorder inheritPadding py="xs">
                                <Group gap="xs">
                                  <Text fw={600} size="sm">{recordName}</Text>
                                  <Badge size="xs" variant="outline">Transaction</Badge>
                                </Group>
                              </Card.Section>
                              <Card.Section inheritPadding py="sm">
                                <KeyValueList
                                  data={recordData as Record<string, any>}
                                  supporting={supportingForRecord}
                                />
                              </Card.Section>
                            </Card>
                          );
                        })}
                      </Stack>
                    ) : (
                      (() => {
                        const data = currentRequest.formData || {};
                        const keys = Object.keys(data).filter((k) => !k.startsWith('_') && k !== 'records');
                        const singleKey = keys.length === 1 ? keys[0] : null;
                        const inner = singleKey && typeof data[singleKey] === 'object' && !Array.isArray(data[singleKey]) ? data[singleKey] : null;
                        const supportingForSingle = singleKey
                          ? supportingRecords[singleKey]
                          || supportingRecords[currentRequest.metadata.recordType]
                          || fallbackSupport
                          : supportingRecords[currentRequest.metadata.recordType]
                          || fallbackSupport;
                        const extraTransactions = Array.isArray(currentRequest.formData._multiTransactions)
                          ? currentRequest.formData._multiTransactions
                          : undefined;

                        const innerWithExtras = inner && extraTransactions
                          ? { ...inner, _multiTransactions: extraTransactions }
                          : inner;

                        const dataWithExtras = extraTransactions
                          ? { ...data, _multiTransactions: extraTransactions }
                          : data;

                        return (
                          <Stack gap="xs">
                            {singleKey && innerWithExtras ? (
                              <>
                                <Group gap="xs">
                                  <Text fw={600} size="sm">{singleKey}</Text>
                                  <Badge size="xs" variant="outline">Transaction</Badge>
                                </Group>
                                <KeyValueList
                                  data={innerWithExtras as Record<string, any>}
                                  supporting={supportingRecords[singleKey] || supportingForSingle}
                                />
                              </>
                            ) : (
                              <KeyValueList
                                data={dataWithExtras as Record<string, any>}
                                supporting={supportingForSingle}
                              />
                            )}
                          </Stack>
                        );
                      })()
                    )}
                  </ScrollArea>
                </Card>
                {/* Audit Trail */}
                <Card withBorder>
                  <Text fw={600} mb="sm">Audit Trail</Text>
                  <Stack gap="xs">
                    <Group>
                      <Text size="sm" c="dimmed">Created:</Text>
                      <Text size="sm">
                        {new Date(currentRequest.audit.createdAt).toLocaleString()} by {(currentRequest.audit as any).createdByName || currentRequest.audit.createdByEmail}
                      </Text>
                    </Group>
                    <Group>
                      <Text size="sm" c="dimmed">Last Updated:</Text>
                      <Text size="sm">
                        {new Date(currentRequest.audit.updatedAt).toLocaleString()} by {(currentRequest.audit as any).updatedByName || currentRequest.audit.updatedByEmail}
                      </Text>
                    </Group>
                    {currentRequest.audit.approvedAt && (
                      <Group>
                        <Text size="sm" c="dimmed">Approved:</Text>
                        <Text size="sm">
                          {new Date(currentRequest.audit.approvedAt).toLocaleString()} by {(currentRequest.audit as any).approvedByName || currentRequest.audit.approvedByEmail}
                        </Text>
                      </Group>
                    )}
                    {/* Rejection audit removed */}
                  </Stack>
                </Card>



                {/* Actions */}
                <Group justify="flex-end">
                  <Tooltip
                    label={approveDisabledReason || 'Approve request'}
                    disabled={!approveDisabledReason}
                  >
                    <span style={{ display: 'inline-block' }}>
                      <Button
                        color="green"
                        leftSection={<IconCheck size={16} />}
                        onClick={() => {
                          if (approveDisabled) {
                            return;
                          }
                          handleApproveRequest();
                        }}
                        loading={approvingRequest}
                        disabled={approveDisabled}
                      >
                        Approve
                      </Button>
                    </span>
                  </Tooltip>
                </Group>

                <Group justify="flex-end" mt="sm">
                  <Tooltip
                    label={deleteDisabledReason || 'Delete request'}
                    disabled={!deleteDisabledReason}
                  >
                    <span style={{ display: 'inline-block' }}>
                      <Button
                        variant="outline"
                        color="red"
                        leftSection={<IconTrash size={16} />}
                        onClick={() => {
                          if (deleteDisabled) {
                            return;
                          }
                          setDeleteModalOpen(true);
                        }}
                        disabled={deleteDisabled}
                      >
                        Delete
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip
                    label={editDisabledReason || 'Edit request'}
                    disabled={!editDisabledReason}
                  >
                    <span style={{ display: 'inline-block' }}>
                      <Button
                        variant="light"
                        leftSection={<IconEdit size={16} />}
                        onClick={() => {
                          if (editDisabled) {
                            return;
                          }
                          const schemaQuery = schemaTypeParam ? `&schemaType=${schemaTypeParam}` : '';
                          navigate(`/geneva_requests/${currentRequest.id}?orgId=${orgId}&templateId=${templateId}${schemaQuery}&tab=edit`);
                        }}
                        disabled={editDisabled}
                      >
                        Edit
                      </Button>
                    </span>
                  </Tooltip>
                </Group>

                <Group justify="center">
                  <Tooltip
                    label={generateDisabledReason || 'Generate XML object'}
                    disabled={!generateDisabledReason}
                  >
                    <span style={{ display: 'inline-block' }}>
                      <Button
                        variant="light"
                        leftSection={<IconDownload size={16} />}
                        onClick={() => {
                          if (generateDisabled) {
                            return;
                          }
                          handleGenerateXmlObject();
                        }}
                        disabled={generateDisabled}
                      >
                        Generate Complete XML Object
                      </Button>
                    </span>
                  </Tooltip>
                  {/* Show undo button for approved requests with Geneva responses */}
                  {currentRequest.status === 'approved' && (currentRequest.geneva_response || currentRequest.geneva_responses) && (
                    <Tooltip
                      label={undoDisabledReason || 'Undo request'}
                      disabled={!undoDisabledReason}
                    >
                      <span style={{ display: 'inline-block' }}>
                        <Button
                          variant="light"
                          color="orange"
                          leftSection={<IconArrowBackUp size={16} />}
                          onClick={() => {
                            if (undoDisabled) {
                              return;
                            }
                            handleUndoRequest(currentRequest);
                          }}
                          loading={undoingRequest === currentRequest.id}
                          disabled={undoDisabled}
                        >
                          Undo Request
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </Group>

                {/* Geneva Response (collapsed, at bottom) */}
                {(currentRequest.geneva_response || currentRequest.geneva_responses) && (
                  <Card withBorder>
                    <Group justify="space-between" onClick={() => setResponseOpen((o) => !o)} style={{ cursor: 'pointer' }}>
                      <Text fw={600}>Geneva Response</Text>
                      <Badge variant="outline" color="gray">{responseOpen ? 'Hide' : 'Show'}</Badge>
                    </Group>
                    <Collapse expanded={responseOpen}>
                      <Box mt="sm">
                        {/* {currentRequest.processing?.errorDetails && (
                          <Alert color="red" variant="light" mb="sm">
                            <Text size="sm">{currentRequest.processing.errorDetails}</Text>
                          </Alert>
                        )} */}
                        {currentRequest.geneva_responses ? (
                          // Multi-record responses (Record<string, GenevaResponse>)
                          <Stack gap="md">
                            <Badge color="blue" variant="light" size="sm">Multi-Transaction Response</Badge>
                            {Object.entries(currentRequest.geneva_responses).map(([recordName, response]) => (
                              <Card key={recordName} withBorder>
                                <Card.Section withBorder inheritPadding py="xs">
                                  <Group gap="xs">
                                    <Text fw={600} size="sm">{recordName}</Text>
                                    <Badge
                                      size="xs"
                                      color={response.status?.toLowerCase() === 'success' ? 'green' : 'red'}
                                      variant="outline"
                                    >
                                      {response.status}
                                    </Badge>
                                  </Group>
                                </Card.Section>
                                <Card.Section inheritPadding py="xs">
                                  <Stack gap="xs">
                                    <Group>
                                      <Text size="sm" c="dimmed">Code:</Text>
                                      <Code style={{ fontSize: '0.75rem' }}>{response.code}</Code>
                                    </Group>
                                    <Group>
                                      <Text size="sm" c="dimmed">Key Value:</Text>
                                      <Code style={{ fontSize: '0.75rem' }}>{response.key_value}</Code>
                                    </Group>
                                    <Group>
                                      <Text size="sm" c="dimmed">Message:</Text>
                                      <Text size="sm">{response.status_message}</Text>
                                    </Group>
                                    {response.timestamp && (
                                      <Group>
                                        <Text size="sm" c="dimmed">Response Time:</Text>
                                        <Text size="sm">
                                          {response.timestamp.toDate?.()?.toLocaleString() ||
                                            new Date(response.timestamp as any).toLocaleString()}
                                        </Text>
                                      </Group>
                                    )}
                                  </Stack>
                                </Card.Section>
                              </Card>
                            ))}
                          </Stack>
                        ) : currentRequest.geneva_response ? (
                          // Regular single response
                          <Stack gap="xs">
                            <Group>
                              <Text size="sm" c="dimmed">Status:</Text>
                              <Badge
                                color={currentRequest.geneva_response.status?.toLowerCase() === 'success' ? 'green' : 'red'}
                                variant="light"
                              >
                                {currentRequest.geneva_response.status}
                              </Badge>
                            </Group>
                            <Group>
                              <Text size="sm" c="dimmed">Code:</Text>
                              <Code>{currentRequest.geneva_response.code}</Code>
                            </Group>
                            <Group>
                              <Text size="sm" c="dimmed">Key Value:</Text>
                              <Code>{currentRequest.geneva_response.key_value}</Code>
                            </Group>
                            <Group>
                              <Text size="sm" c="dimmed">Message:</Text>
                              <Text size="sm">{currentRequest.geneva_response.status_message}</Text>
                            </Group>
                            {currentRequest.geneva_response.timestamp && (
                              <Group>
                                <Text size="sm" c="dimmed">Response Time:</Text>
                                <Text size="sm">
                                  {currentRequest.geneva_response.timestamp.toDate?.()?.toLocaleString() ||
                                    new Date(currentRequest.geneva_response.timestamp as any).toLocaleString()}
                                </Text>
                              </Group>
                            )}
                          </Stack>
                        ) : null}
                      </Box>
                    </Collapse>
                  </Card>
                )}
              </Stack>
            );
          }

          if (currentBulkRequest) {
            return (
              <Stack gap="md">
                {/* Bulk Request Info */}
                <Card withBorder>
                  <Stack gap="xs">
                    <Group justify="space-between">
                      <Text fw={500}>Request ID:</Text>
                      <Code>{currentBulkRequest.id}</Code>
                    </Group>
                    <Group justify="space-between">
                      <Text fw={500}>Type:</Text>
                      <Badge color="blue" leftSection={<IconFileSpreadsheet size={12} />}>
                        Bulk Request
                      </Badge>
                    </Group>
                    <Group justify="space-between">
                      <Text fw={500}>TRX Type:</Text>
                      <Badge>{currentBulkRequest.recordType}</Badge>
                    </Group>
                    <Group justify="space-between">
                      <Text fw={500}>Status:</Text>
                      <Badge
                        leftSection={getStatusIcon(currentBulkRequest.status)}
                        color={getStatusColor(currentBulkRequest.status)}
                      >
                        {currentBulkRequest.status}
                      </Badge>
                    </Group>
                    <Group justify="space-between">
                      <Text fw={500}>Total Records:</Text>
                      <Text>{currentBulkRequest.metadata.totalRecords}</Text>
                    </Group>
                    <Group justify="space-between">
                      <Text fw={500}>Valid Records:</Text>
                      <Text c="green">{currentBulkRequest.metadata.validRecords}</Text>
                    </Group>
                    {currentBulkRequest.metadata.errorRecords > 0 && (
                      <Group justify="space-between">
                        <Text fw={500}>Error Records:</Text>
                        <Text c="red">{currentBulkRequest.metadata.errorRecords}</Text>
                      </Group>
                    )}
                    <Group justify="space-between">
                      <Text fw={500}>Source File:</Text>
                      <Text>{currentBulkRequest.parsedData.fileName}</Text>
                    </Group>
                    {currentBulkRequest.geneva_responses && (
                      <Group justify="space-between">
                        <Text fw={500}>Geneva Status:</Text>
                        {getGenevaStatusBadge(currentBulkRequest.geneva_responses)}
                      </Group>
                    )}
                  </Stack>
                </Card>

                {/* Geneva Responses moved to bottom as collapsible */}

                {/* Field Mappings */}
                <Card withBorder>
                  <Text fw={600} mb="sm">Field Mappings</Text>
                  <ScrollArea h={200}>
                    <Table>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>Geneva Field</Table.Th>
                          <Table.Th>Mapped Column</Table.Th>
                          <Table.Th>Type</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {Object.entries(currentBulkRequest.fieldMappings).map(([fieldName, mapping]) => (
                          <Table.Tr key={fieldName}>
                            <Table.Td>
                              <Group gap="xs">
                                <Text size="sm" fw={500}>{fieldName}</Text>
                                {mapping.isRequired && (
                                  <Badge size="xs" color="red" variant="outline">Required</Badge>
                                )}
                              </Group>
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm">{mapping.columnName}</Text>
                            </Table.Td>
                            <Table.Td>
                              <Badge size="xs" variant="outline">{mapping.dataType}</Badge>
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </ScrollArea>
                </Card>

                {/* Audit Trail */}
                <Card withBorder>
                  <Text fw={600} mb="sm">Audit Trail</Text>
                  <Stack gap="xs">
                    <Group>
                      <Text size="sm" c="dimmed">Created:</Text>
                      <Text size="sm">
                        {new Date(currentBulkRequest.audit.createdAt).toLocaleString()} by {(currentBulkRequest.audit as any).createdByName || currentBulkRequest.audit.createdByEmail}
                      </Text>
                    </Group>
                    {currentBulkRequest.audit.updatedAt && (
                      <Group>
                        <Text size="sm" c="dimmed">Last Updated:</Text>
                        <Text size="sm">
                          {new Date(currentBulkRequest.audit.updatedAt).toLocaleString()} by {(currentBulkRequest.audit as any).updatedByName || currentBulkRequest.audit.updatedByEmail}
                        </Text>
                      </Group>
                    )}
                    {currentBulkRequest.audit.approvedAt && (
                      <Group>
                        <Text size="sm" c="dimmed">Approved:</Text>
                        <Text size="sm">
                          {new Date(currentBulkRequest.audit.approvedAt).toLocaleString()} by {(currentBulkRequest.audit as any).approvedByName || currentBulkRequest.audit.approvedByEmail}
                        </Text>
                      </Group>
                    )}
                    {/* Rejection audit removed */}
                  </Stack>
                </Card>

                {/* Bulk Data Preview */}
                <BulkDataPreview
                  data={typeof currentBulkRequest.bulkFormData === 'string'
                    ? JSON.parse(currentBulkRequest.bulkFormData)
                    : currentBulkRequest.bulkFormData as Record<string, any>[]}
                  fieldMappings={currentBulkRequest.fieldMappings}
                  title="Bulk Request Data"
                  maxHeight={400}
                  showRowLimit={true}
                />

                {/* Actions */}
                {canApprove && currentBulkRequest.status === 'pending' && (
                  <Group justify="flex-end">
                    <Button
                      color="green"
                      leftSection={<IconCheck size={16} />}
                      loading={approvingRequest}
                      disabled={approvingRequest}
                      onClick={async () => {
                        if (!requireApprovePermission('approve Geneva requests')) {
                          return;
                        }
                        // Handle bulk approval
                        if (!selectedBulkRequest || !userContext.user || approvingRequest) return;

                        setApprovingRequest(true);

                        try {

                          notifications.show({
                            id: 'xml-generation',
                            title: 'Generating XML',
                            message: `Generating XML file for bulk request ${currentBulkRequest.id}...`,
                            color: 'blue',
                            loading: true,
                            autoClose: false,
                          });

                          const result = await generateXml({ request: currentBulkRequest, orgId, isApproval: true });

                          notifications.update({
                            id: 'xml-generation',
                            title: 'XML Generated',
                            message: `XML file generated successfully for bulk request ${currentBulkRequest.id}`,
                            color: 'green',
                            icon: <IconCheck size={16} />,
                            loading: false,
                            autoClose: 3000,
                          });

                          console.log('Bulk XML generation result:', result);
                          setLastGenevaError(null);

                          // Automatically generate XML after approval
                        } catch (xmlError) {
                          console.error('Error generating bulk XML:', xmlError);
                          showGenevaXmlGenerationError(xmlError, currentBulkRequest.id);
                        }

                        const requestRef = doc(db, 'geneva_bulk_requests', currentBulkRequest.id);
                        updateDoc(requestRef, {
                          status: 'approved',
                          'audit.approvedAt': Date.now(),
                          'audit.approvedBy': userContext.user.uid,
                          'audit.approvedByEmail': userContext.user.email,
                          'audit.approvedByName': userContext.user.displayName,
                          'audit.updatedAt': Date.now(),
                          'audit.updatedBy': userContext.user.uid,
                          'audit.updatedByEmail': userContext.user.email,
                          'audit.updatedByName': userContext.user.displayName,
                          updatedAt: Timestamp.now(),
                        }).then(async () => {
                          // Show success notification
                          notifications.show({
                            title: 'Bulk Request Approved',
                            message: `Bulk request ${currentBulkRequest.id} has been approved`,
                            color: 'green',
                            icon: <IconCheck size={16} />,
                          });



                          setDetailsModal(false);
                          setSelectedBulkRequest(null);
                        }).catch((error) => {
                          console.error('Error approving bulk request:', error);
                          notifications.show({
                            title: 'Approval Failed',
                            message: 'Failed to approve bulk request',
                            color: 'red',
                          });
                        }).finally(() => {
                          setApprovingRequest(false);
                        });
                      }}
                    >
                      Approve Bulk Request
                    </Button>
                  </Group>
                )}

                {/* Undo button for approved bulk requests */}
                {canCreate && currentBulkRequest.status === 'approved' && currentBulkRequest.geneva_responses && (
                  <Group justify="center" mt="md">
                    <Button
                      variant="light"
                      color="orange"
                      leftSection={<IconArrowBackUp size={16} />}
                      onClick={() => handleUndoRequest({
                        id: currentBulkRequest.id,
                        isBulk: true,
                      } as any)}
                      loading={undoingRequest === currentBulkRequest.id}
                      disabled={undoingRequest === currentBulkRequest.id}
                    >
                      Undo Bulk Request
                    </Button>
                  </Group>
                )}

                {/* Allow delete for non-approved bulk requests */}
                {canCreate && currentBulkRequest.status !== 'approved' && (
                  <Group justify="flex-end" mt="md">
                    <Button
                      variant="outline"
                      color="red"
                      leftSection={<IconTrash size={16} />}
                      onClick={() => setDeleteModalOpen(true)}
                    >
                      Delete Bulk Request
                    </Button>
                  </Group>
                )}

                {/* Geneva Responses (collapsed, at bottom) */}
                {currentBulkRequest.geneva_responses && (
                  <Card withBorder>
                    <Group justify="space-between" onClick={() => setBulkResponseOpen((o) => !o)} style={{ cursor: 'pointer' }}>
                      <Text fw={600}>Geneva Responses</Text>
                      <Badge variant="outline" color="gray">{bulkResponseOpen ? 'Hide' : 'Show'}</Badge>
                    </Group>
                    <Collapse expanded={bulkResponseOpen}>
                      <Box mt="sm">
                        {currentBulkRequest.processing?.errorDetails && (
                          <Alert color="red" variant="light" mb="sm">
                            <Text size="sm">{currentBulkRequest.processing.errorDetails}</Text>
                          </Alert>
                        )}
                        <ScrollArea h={300}>
                          <Table>
                            <Table.Thead>
                              <Table.Tr>
                                <Table.Th>Row</Table.Th>
                                <Table.Th>Status</Table.Th>
                                <Table.Th>Code</Table.Th>
                                <Table.Th>Key Value</Table.Th>
                                <Table.Th>Message</Table.Th>
                              </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                              {Object.entries(currentBulkRequest.geneva_responses).map(([index, responseOrResponses]) => {
                                if (responseOrResponses && typeof responseOrResponses === 'object' && !('status' in responseOrResponses)) {
                                  return Object.entries(responseOrResponses as Record<string, GenevaResponse>).map(([recordName, response]) => (
                                    <Table.Tr key={`${index}-${recordName}`}>
                                      <Table.Td>
                                        <Group gap="xs">
                                          <Text size="sm" fw={500}>{parseInt(index) + 1}</Text>
                                          {Object.keys(responseOrResponses).length > 1 && (
                                            <Badge size="xs" variant="outline" color="blue">
                                              {recordName}
                                            </Badge>
                                          )}
                                        </Group>
                                      </Table.Td>
                                      <Table.Td>
                                        <Badge size="xs" color={response.status?.toLowerCase() === 'success' ? 'green' : 'red'} variant="light">
                                          {response.status}
                                        </Badge>
                                      </Table.Td>
                                      <Table.Td>
                                        <Code style={{ fontSize: '0.75rem' }}>{response.code}</Code>
                                      </Table.Td>
                                      <Table.Td>
                                        <Code style={{ fontSize: '0.75rem' }}>{response.key_value}</Code>
                                      </Table.Td>
                                      <Table.Td>
                                        <Text size="xs" style={{ maxWidth: '200px' }} truncate>
                                          {response.status_message}
                                        </Text>
                                      </Table.Td>
                                    </Table.Tr>
                                  ));
                                } else {
                                  const response = responseOrResponses as GenevaResponse;
                                  return (
                                    <Table.Tr key={index}>
                                      <Table.Td>
                                        <Text size="sm" fw={500}>{parseInt(index) + 1}</Text>
                                      </Table.Td>
                                      <Table.Td>
                                        <Badge size="xs" color={response.status?.toLowerCase() === 'success' ? 'green' : 'red'} variant="light">
                                          {response.status}
                                        </Badge>
                                      </Table.Td>
                                      <Table.Td>
                                        <Code style={{ fontSize: '0.75rem' }}>{response.code}</Code>
                                      </Table.Td>
                                      <Table.Td>
                                        <Code style={{ fontSize: '0.75rem' }}>{response.key_value}</Code>
                                      </Table.Td>
                                      <Table.Td>
                                        <Text size="xs" style={{ maxWidth: '200px' }} truncate>
                                          {response.status_message}
                                        </Text>
                                      </Table.Td>
                                    </Table.Tr>
                                  );
                                }
                              }).flat()}
                            </Table.Tbody>
                          </Table>
                        </ScrollArea>
                      </Box>
                    </Collapse>
                  </Card>
                )}
              </Stack>
            );
          }

          return null;
        })()}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Delete Request"
      >
        <Stack gap="md">
          <Text>
            Are you sure you want to delete this {selectedBulkRequest ? 'bulk ' : ''}request? This action cannot be undone.
          </Text>
          <Alert color="orange" variant="light">
            Deletion is only allowed for requests that are not approved.
          </Alert>
          <Group justify="flex-end">
            <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button color="red" leftSection={<IconTrash size={16} />} onClick={handleDeleteRequest}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
};

export default GenevaRequests;
